/**
 * 新しい差出人の「送り始めの 1 日の上限」（warmup）の判断だけを集めた純関数（React・i18n・fetch に依存しない。2026-09-27 creator/mail-warmup）。
 *
 * 背景（Core の mail-hardening・apps/server の modules/mailBroadcast/warmup.ts）:
 *   新しい差出人は、最初に送り始めた時刻から 7 日間（24 時間ごとの「日」）、1 日に SES へ渡す通数に上限がある（1 日目 1,000 → 2 倍ずつ）。
 *   超えた分は 400 にせず**翌日に回す**。配信は送り切るまで `sending` のまま（counts.pending = まだ送っていない数）。
 *   Core は数と時刻（UTC の ISO）だけを返し、文言と「現地時刻での見せ方」は Creator の画面が決める。
 *
 * WHY mailLogic.ts と分けるか: mailLogic.ts は 300 行を超えている（ファイル肥大の目安）。warmup は「上限の数をどう読むか」だけの
 *   まとまった話で、画面の 3 か所（書く・確認・履歴）から使う。mailLogic と同じく import を持たない = Node の型の除去だけでテストが動く。
 *
 * **苦情の線・上限の数字の意味（なぜ 1,000 か等）は画面で説明しない**（Core の方針: すり抜けの方法を教えない）。出すのは今日の上限・残り・期間の終わりだけ。
 */

/**
 * GET …/mail/status の `warmup` と、今すぐ送るの応答 `{ broadcast, warmup }` の `warmup`（Core の warmupForDisplay と同じ欄）。
 * 期間を過ぎた（上限なし）ときは null。時刻はすべて UTC の ISO。
 */
export interface MailWarmup {
  /** 何日目か（1 から）。まだ送り始めていなければ 1 */
  day: number;
  /** 上限のある日数（既定 7） */
  days: number;
  dailyLimit: number;
  sentToday: number;
  remainingToday: number;
  /** 最初に送り始めた時刻。まだ送っていなければ null（最初に送った時点から 1 日目） */
  startedAt: string | null;
  /** 次の「日」の始まり（= 翌日に回った分を送り始める時刻）。まだ送っていなければ null */
  nextDayAt: string | null;
  /** 上限の期間の終わり。まだ送っていなければ null */
  endsAt: string | null;
  /** 今日の上限に達して止めている間はその終わり。止めていなければ null */
  pausedUntil: string | null;
  /** 今すぐ送るの応答だけ: この配信のうち今日は送らず翌日以降に回る見込みの通数 */
  deferred?: number;
  /**
   * 2026-09-27 creator/mail-identity（Core の mail-prelaunch・team/core 28a0867）: 払い出しの本人確認が済んでいるか。
   * 済んでいない開発者は 1 日の上限が min(その日の warmup の上限, 未確認の上限) になり、8 日目以降も上限が続く（warmup が null にならない）。
   * 古いサーバーでは欄が無い = undefined（本人確認の話をしない = 以前の表示のまま）
   */
  identityVerified?: boolean;
  /** 今日の上限を決めているもの（同じ値なら 'warmup'）。古いサーバーでは undefined */
  limitedBy?: 'warmup' | 'identity';
  /** 本人確認が済んでいない間の上限（済んでいれば null）。古いサーバーでは undefined */
  unverifiedDailyLimit?: number | null;
}

const isNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
const isoOrNull = (v: unknown): string | null => (typeof v === 'string' && v ? v : null);

/**
 * 応答の warmup を読む。無い・null・形が違う（古いサーバー・別の応答）なら null = 帯を出さない。
 * WHY 形を確かめるか: 上限の帯は「送る前の判断」に使う数。欄が欠けた応答を 0 として読むと「今日は残り 0 通」と誤って出る。
 *   読めないものは「上限なし」と同じ扱い（サーバーは上限を超えた分を翌日に回すだけで、断らない = 出し損ねても送信は壊れない）
 */
export function readWarmup(v: unknown): MailWarmup | null {
  if (v == null || typeof v !== 'object') return null;
  const w = v as Record<string, unknown>;
  if (!isNum(w.dailyLimit) || !isNum(w.remainingToday) || !isNum(w.day) || !isNum(w.days)) return null;
  return {
    day: w.day,
    days: w.days,
    dailyLimit: w.dailyLimit,
    sentToday: isNum(w.sentToday) ? w.sentToday : Math.max(0, w.dailyLimit - w.remainingToday),
    remainingToday: Math.max(0, w.remainingToday),
    startedAt: isoOrNull(w.startedAt),
    nextDayAt: isoOrNull(w.nextDayAt),
    endsAt: isoOrNull(w.endsAt),
    pausedUntil: isoOrNull(w.pausedUntil),
    ...(isNum(w.deferred) ? { deferred: Math.max(0, w.deferred) } : {}),
    // 本人確認の欄は形が正しいときだけ写す（欠けた / 違う形 = 古いサーバー = 以前の「送り始めの上限」の表示のまま）
    ...(typeof w.identityVerified === 'boolean' ? { identityVerified: w.identityVerified } : {}),
    ...(w.limitedBy === 'warmup' || w.limitedBy === 'identity' ? { limitedBy: w.limitedBy } : {}),
    ...(isNum(w.unverifiedDailyLimit) || w.unverifiedDailyLimit === null ? { unverifiedDailyLimit: w.unverifiedDailyLimit as number | null } : {})
  };
}

/**
 * 上限の説明をどちらの話として書くか（2026-09-27 creator/mail-identity）。
 * - 'warmup'  … 送り始めの 7 日間の上限（期間の終わりがある。以前の表示）
 * - 'identity' … 本人確認が済むまで続く上限（期間の終わりが無い）
 * WHY limitedBy だけでなく identityVerified === false でも 'identity' にするか: 未確認の上限は 1 日目の warmup と同じ数（どちらも 1,000）なので、
 *   1 日目・送り始める前は limitedBy が 'warmup' になる。そこで「最初の 7 日間は」「上限は○日までです」と出すと、8 日目以降も上限が続く
 *   本人には誤り（実際には終わらない）。未確認なら最初から「本人確認が済むまでの上限」として書く
 */
export function limitKind(w: MailWarmup | null): 'warmup' | 'identity' | null {
  if (!w) return null;
  return w.identityVerified === false || w.limitedBy === 'identity' ? 'identity' : 'warmup';
}

/**
 * 本人確認（受け取りの設定）を済ませると 1 日の上限が上がる / 外れる = 案内とボタンを出すか。欄の無い古いサーバーでは出さない。
 *
 * payoutsHidden（2026-09-29 creator/mail-close・Money の指摘の 1 と 2）: 受け取りの設定（出金 = payouts）を最初の公開で見せない間は false。
 *   WHY: 行き先のページ（/money/live/get-paid）が閉じている = 「受け取りの設定で上限が上がる」の案内もボタンも行き止まりになる。
 *   運営がメール（mail）だけを開けても payouts が閉じていれば同じ（サーバーの起動の検査は mail を開けるなら payouts も、を求めるが、
 *   画面は画面で守る = 二重の守り）。そのときの帯は「1 日に送れる数に上限があります」（identityBody / identityNotStarted）だけになる。
 * WHY 引数で受け取るか（launchHidden.ts を import しないか）: このファイルは import を持たない約束（冒頭・Node の型の除去だけでテストが動く）。
 *   呼ぶ側（pages/mail/WarmupBand.tsx）が isMoneyNavHidden('get-paid') を渡す。省略 = false = 以前の動き
 */
export const needsIdentity = (w: MailWarmup | null, payoutsHidden = false): boolean => !payoutsHidden && w?.identityVerified === false;

/**
 * 帯に「上限は○まで」を出すか。未確認（limitKind = 'identity'）の間は出さない（上限は終わらない）。
 * 過去の時刻も出さない: 本人確認が済んだ直後など、サーバーが期間の終わりを過ぎた endsAt を返しても「○までです」と過去を案内しない
 */
export function showEndsAt(w: MailWarmup | null, now: Date = new Date()): boolean {
  if (!w?.endsAt || limitKind(w) !== 'warmup') return false;
  const t = new Date(w.endsAt).getTime();
  return Number.isFinite(t) && t > now.getTime();
}

/**
 * 今すぐ送ると、今日の残りを超える通数（= 翌日以降に回る見込み）。上限の期間外・宛先の数が不明なら 0。
 * 確認のダイアログ（画面）・MCP の確認フォームで「一部は翌日以降に送られます」を足すかの判断に使う。
 * 見込み: 送る時点で撤回・年齢で宛先が減ることはある（Core の deferred と同じ考え方）
 */
export function overToday(w: MailWarmup | null, recipients: number | null | undefined): number {
  if (!w || recipients == null) return 0;
  return Math.max(0, recipients - w.remainingToday);
}

/**
 * 今すぐ送るの応答 `{ broadcast, warmup }` から「翌日に回った数と、続きを送り始める時刻」。回っていなければ null。
 * 時刻は nextDayAt（今日の上限に達して止めていれば pausedUntil = 同じ時刻）
 */
export function sendDeferred(res: unknown): { deferred: number; at: string | null; identity: boolean } | null {
  if (res == null || typeof res !== 'object') return null;
  const w = readWarmup((res as Record<string, unknown>).warmup);
  if (!w?.deferred) return null;
  // identity: 翌日に回った理由を「送り始めの上限」ではなく「1 日の上限」と書くか（limitKind の WHY）
  return { deferred: w.deferred, at: w.pausedUntil ?? w.nextDayAt, identity: limitKind(w) === 'identity' };
}

/**
 * 履歴の配信のうち「翌日に回った」通数（0 = 出さない）。
 * 条件: 送信中（sending）・まだ送っていない行がある（counts.pending > 0）・上限の期間中で、今日の上限に達している（止めている / 残り 0）。
 * WHY 上限に達していることまで見るか（指示書は「sending かつ pending > 0」）: 上限に関係なく、送り始めた直後の配信は
 *   Queue が 1 通ずつ送る間 pending が 0 でない。そこで「翌日に回った」と出すと、実際には数分で届く配信を「明日」と誤って伝える。
 *   上限に達していない間の pending は、詳細の「送信待ち」の欄（HistoryCard の既存の表示）で足りる
 */
export function carriedOver(b: { status: string; counts?: { pending?: number | null } | null }, w: MailWarmup | null): number {
  const pending = b.counts?.pending ?? 0;
  if (b.status !== 'sending' || pending <= 0 || !w) return 0;
  if (!w.pausedUntil && w.remainingToday > 0) return 0;
  return pending;
}

/** 翌日に回った分を送り始める時刻（止めていればその終わり・無ければ次の日の始まり） */
export const resumeAt = (w: MailWarmup | null): string | null => (w ? (w.pausedUntil ?? w.nextDayAt) : null);
